import { cva, type VariantProps } from 'class-variance-authority';
import { format, isValid, parse, parseISO } from 'date-fns';
import * as React from 'react';

import { cn } from './lib/utils';

/**
 * Visual tone for the milestone eyebrow + date. Echoes the app's status
 * colour language (blue = active / released, emerald = done / complete) while
 * keeping `neutral` and `muted` for lifecycle endpoints that shouldn't shout.
 */
export type LabeledDateTone = 'neutral' | 'info' | 'success' | 'muted';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Normalise the shapes a date prop can take into a `Date`. A bare
 * `YYYY-MM-DD` string is read as a *local* calendar day (not UTC midnight) so
 * a Postgres `date` column doesn't render as the previous day in negative
 * timezones; everything else goes through `parseISO`. Returns null for empty
 * or unparseable input.
 */
function toDate(value: Date | string | null | undefined): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const parsed = DATE_ONLY.test(value) ? parse(value, 'yyyy-MM-dd', new Date()) : parseISO(value);
  return isValid(parsed) ? parsed : null;
}

const eyebrowVariants = cva(
  'inline-flex items-center gap-1.5 text-[0.625rem] leading-none font-semibold tracking-[0.08em] uppercase',
  {
    variants: {
      tone: {
        neutral: 'text-muted-foreground',
        info: 'text-blue-600 dark:text-blue-400',
        success: 'text-emerald-600 dark:text-emerald-400',
        muted: 'text-muted-foreground/70',
      } satisfies Record<LabeledDateTone, string>,
    },
    defaultVariants: { tone: 'neutral' },
  },
);

/** Value-line colour per tone. Archived (`muted`) recedes and is struck. */
const valueToneClass: Record<LabeledDateTone, string> = {
  neutral: 'text-foreground',
  info: 'text-foreground',
  success: 'text-foreground',
  muted: 'text-muted-foreground line-through decoration-muted-foreground/30',
};

export interface LabeledDateProps
  extends Omit<React.ComponentProps<'div'>, 'children'>, VariantProps<typeof eyebrowVariants> {
  /** Eyebrow label naming the milestone, e.g. "Released". */
  label: string;
  /** The milestone date. Accepts a `Date`, an ISO timestamp, or `YYYY-MM-DD`. */
  date: Date | string | null | undefined;
  /** date-fns format string for the value line. Defaults to e.g. "8 Jun 2026". */
  dateFormat?: string;
  /** Rendered in place of the date when it is null / unparseable. */
  emptyText?: string;
  /** Lay the label and date side-by-side instead of stacked. */
  orientation?: 'stacked' | 'inline';
  /** Show the leading tone-coloured status pip. */
  showDot?: boolean;
}

/**
 * A compact two-tier "eyebrow label + date": a tone-coloured milestone name
 * (with a status pip) above a crisp, tabular-aligned date. Purely
 * presentational — callers decide which label / date / tone to pass, so the
 * same primitive renders "Created", "Released", "Completed", or "Archived".
 */
function LabeledDate({
  label,
  date,
  tone = 'neutral',
  dateFormat = 'd MMM yyyy',
  emptyText = '—',
  orientation = 'stacked',
  showDot = true,
  className,
  ...props
}: LabeledDateProps) {
  const safeTone: LabeledDateTone = tone ?? 'neutral';
  const resolved = toDate(date);
  const formatted = resolved ? format(resolved, dateFormat) : null;

  return (
    <div
      data-slot="labeled-date"
      data-tone={safeTone}
      className={cn(
        'min-w-0',
        orientation === 'stacked' ? 'flex flex-col gap-1' : 'inline-flex items-baseline gap-2',
        className,
      )}
      {...props}
    >
      <span className={cn(eyebrowVariants({ tone: safeTone }))}>
        {showDot ? (
          <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
        ) : null}
        {label}
      </span>
      {formatted ? (
        <time
          dateTime={format(resolved as Date, 'yyyy-MM-dd')}
          className={cn('text-[0.8125rem] tabular-nums', valueToneClass[safeTone])}
        >
          {formatted}
        </time>
      ) : (
        <span className="text-muted-foreground text-[0.8125rem] tabular-nums">{emptyText}</span>
      )}
    </div>
  );
}

export { LabeledDate, eyebrowVariants };
